import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatDate } from "./dateUtils";
import { message } from "antd";
import {
  normalizeVariant,
  getShipmentWarningsByVariant,
  shipmentWarningYesNoLabel,
  SHIPMENT_WARNING_KIND,
} from "./shipmentsTransformers";

const getDatosText = (check, telemetryTypeCode) => {
  switch (check) {
    case 0:
      return "No Gestionado";
    case 1:
      if (telemetryTypeCode === 3) return "Offline (PDF)";
      if (telemetryTypeCode === 4) return "Online";
      if (telemetryTypeCode === 5) return "Offline (Datos)";
      return "No Gestionado";
    case 2:
      if (telemetryTypeCode === 1) return "Sin Grabación";
      if (telemetryTypeCode === 2) return "Sin Datalogger";
      return "No Gestionado";
    case 3:
      return "Online (Envío sin Admisión)";
    default:
      return "No Gestionado";
  }
};

const fieldDefinitions = [
  { key: "shipmentCode", label: "CÓDIGO DE BULTO" },
  {
    key: "imei",
    label: "IMEI (MAC)",
    transform: (item) =>
      item.macMando && item.macMando !== "N/D"
        ? `${item.imei} (${item.macMando})`
        : item.imei,
  },
  { key: "numRefClient", label: "REF. CLIENTE / PEDIDO" },
  {
    key: "telemetryTypeCode",
    label: "DATOS",
    transform: (item) => getDatosText(item.check, item.telemetryTypeCode),
  },
  {
    key: "temperatureRange",
    label: "RANGO DE TEMPERATURA",
    transform: (item) => item.temperatureRange,
  },
  {
    key: "warningsTempTotal",
    label: "ALERTAS (TEMP)",
    transform: (item) => shipmentWarningYesNoLabel(item.warningsTempTotal ?? 0),
  },
  {
    key: "warningsImpactTotal",
    label: "ALERTAS (IMPACTO)",
    transform: (item) =>
      shipmentWarningYesNoLabel(item.warningsImpactTotal ?? 0),
  },
  {
    key: "warningsOpenTotal",
    label: "ALERTAS (APERTURA)",
    transform: (item) => shipmentWarningYesNoLabel(item.warningsOpenTotal ?? 0),
  },
  { key: "senderName", label: "REMITENTE" },
  {
    key: "eventName",
    label: "ÚLTIMO EVENTO",
  },
  {
    key: "eventDate",
    label: "FECHA Y HORA",
    transform: (item) => formatDate(item.eventDate),
  },
  { key: "destinationName", label: "DESTINATARIO" },
  {
    key: "startDate",
    label: "FECHA ALTA EN PLATAFORMA",
    transform: (item) => formatDate(item.startDate),
  },
];

const formatItem = (item) =>
  fieldDefinitions.reduce((acc, { key, label, transform }) => {
    acc[label] = transform ? transform(item) : item[key] || "N/D";
    return acc;
  }, {});

const getTimestamp = () => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const parts = formatter.formatToParts(now);
  const map = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${map.year}${map.month}${map.day}_${map.hour}${map.minute}${map.second}`;
};

const applyTelemetryVariantToRows = (data, telemetryVariant) => {
  const variant = normalizeVariant(telemetryVariant);

  return (data || []).map((item) => ({
    ...item,
    warningsTempTotal: getShipmentWarningsByVariant(
      item,
      variant,
      SHIPMENT_WARNING_KIND.TEMP,
    ),
    warningsImpactTotal: getShipmentWarningsByVariant(
      item,
      variant,
      SHIPMENT_WARNING_KIND.IMPACT,
    ),
    warningsOpenTotal: getShipmentWarningsByVariant(
      item,
      variant,
      SHIPMENT_WARNING_KIND.OPEN,
    ),
  }));
};

export function exportData(data, fileType, telemetryVariant = "full") {
  try {
    const variant = normalizeVariant(telemetryVariant);

    const preparedData = applyTelemetryVariantToRows(data, variant);

    const formattedData = preparedData.map(formatItem);
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");

    const timestamp = getTimestamp();

    const variantLabel = variant === "reduced" ? "Reducido" : "Completo";
    const ext = fileType === "excel" ? "xlsx" : "csv";
    const fileName = `DatosEnvios_${variantLabel}_${timestamp}.${ext}`;

    if (fileType === "csv") {
      const csv = XLSX.utils.sheet_to_csv(worksheet, { FS: ";" });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, fileName);
    } else if (fileType === "excel") {
      XLSX.writeFile(workbook, fileName, { bookType: "xlsx" });
    } else {
      throw new Error("Tipo de archivo no soportado");
    }

    message.success(`Archivo descargado correctamente: ${fileName}`);
  } catch (error) {
    console.error("Error exportando datos:", error);
    message.error(
      `Error al exportar los datos en formato ${String(fileType).toUpperCase()}`,
    );
  }
}
